import { useCallback, useEffect, useState } from 'react';
import { DataGrid, type GridColDef } from '@mui/x-data-grid';
import { useSelectedAccount } from '../App';
import { api } from '../api/apiClient';
//import DateRangeFilter from '../components/dateRangeFilters';
import IconButton from '../components/iconButton';
import Tooltip from '@mui/material/Tooltip';

import DownloadIcon from '@mui/icons-material/DownloadOutlined';
import PreviewIcon from '@mui/icons-material/VisibilityOutlined';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';

interface DocumentDto {
    id: number;
    name: string;
    date: string;
    sizeBytes: number;
    type: string;
}

interface DocumentUi {
    id: number;
    name: string;
    date: string;
    rawDate: string;
    size: number;
    formattedSize: string;
}

const MyDocuments = () => {
    const { selectedAccount } = useSelectedAccount();

    const [documents, setDocuments] = useState<DocumentUi[]>([]);
    const [loading, setLoading] = useState(false);
    const [fromDate, setFromDate] = useState('1970-01-01');
    const [toDate, setToDate] = useState('2100-01-01');
    //const [resetTrigger, setResetTrigger] = useState(false);
    const [searchText, setSearchText] = useState('');

    const formatDateShort = (iso: string | null | undefined) => {
        if (!iso) return '';
        const d = new Date(iso);
        return isNaN(d.getTime()) ? '' : d.toLocaleDateString(undefined, {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        });
    };

    const getFileIcon = () => {
        return <PictureAsPdfIcon fontSize="small" />;
    };

    const formatSize = (bytes: number) => {
        if (bytes < 1024) return `${bytes} B`;
        if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
        return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
    };

    const loadDocuments = useCallback(() => {
        // ToDo: Remove when reintroducing the filters. Keep here for now to stop ts from crying when building
        setFromDate('1970-01-01');
        setToDate('2100-01-01');
        setSearchText('');

        if (!selectedAccount?.accountReference) {
            setDocuments([]);
            return;
        }

        setLoading(true);

        const controller = new AbortController();
        const signal = controller.signal;

        let isCurrent = true;

        const params = {
            rentAccountId: selectedAccount.accountReference,
            type: 'AnnualChargeNotification',
            name: searchText || '',
            fromDate,
            toDate
        };

        api<DocumentDto[]>('/documents', { method: 'POST', body: JSON.stringify(params), signal, }).then((data) => {
            if (!isCurrent) {
                return;
            }

            const ui: DocumentUi[] = data.map((d) => ({
                id: d.id,
                name: d.name,
                date: formatDateShort(d.date),
                rawDate: d.date,
                size: d.sizeBytes,
                formattedSize: formatSize(d.sizeBytes)
            })).sort((a, b) => new Date(b.rawDate).getTime() - new Date(a.rawDate).getTime());

            setDocuments(ui);
        }).catch((err) => {
            if (err.name === 'AbortError') {
                console.log('Previous request canceled');
            } else {
                console.error('Failed to load documents', err);
                setDocuments([]);
            }
        }).finally(() => {
            if (isCurrent) {
                setLoading(false);
            }
        });

        return () => {
            isCurrent = false;
            controller.abort();
        };
    }, [selectedAccount, searchText, fromDate, toDate]);

    useEffect(() => {
        const abortLoad = loadDocuments();
        return () => abortLoad?.();
    }, [loadDocuments]);

    //const handleFilterChange = (from: string, to: string) => {
    //    setFromDate(from || '1970-01-01');
    //    setToDate(to || '2100-01-01');
    //};

    //const handleReset = () => {
    //    setSearchText('');
    //    setFromDate('1970-01-01');
    //    setToDate('2100-01-01');
    //    setResetTrigger(prev => !prev);
    //    loadDocuments();
    //};

    const handleAction = async (action: 'download' | 'preview', doc: DocumentUi) => {
        try {
            const url = `/documents/${doc.id}/download${action === 'preview' ? '?inline=true' : ''}`;
            const blob = await api<Blob>(url, { responseType: 'blob' });

            const mimeType = 'application/pdf';
            const blobWithType = new Blob([blob], { type: mimeType });
            const blobUrl = URL.createObjectURL(blobWithType);

            if (action === 'preview') {
                window.open(blobUrl, '_blank', 'noopener,noreferrer');
            } else {
                const a = document.createElement('a');
                a.href = blobUrl;
                a.download = `${doc.name}.pdf`;
                document.body.appendChild(a);
                a.click();
                a.remove();
            }

            setTimeout(() => URL.revokeObjectURL(blobUrl), 10000);
        } catch (err) {
            console.error('Failed to fetch document', err);
        }
    };

    const columns: GridColDef[] = [
        { field: 'date', headerName: 'Date',minWidth: 110, sortable: true },
        {
            field: 'name',
            headerName: 'Description',
            flex: 1,
            minWidth: 150,
            sortable: true,
            renderCell: (params: any) => (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    {getFileIcon()}
                    <span>{params.value}</span>
                </div>
            )
        },
        {
            field: 'formattedSize',
            headerName: 'Size',
            width: 100,
        },
        {
            field: 'actions',
            headerName: '',
            width: 120,
            sortable: false,
            renderCell: (params: any) => {
                return (
                    <div className="page__table-cell--icons">
                        <Tooltip title="Download Document" placement="bottom" arrow>
                            <span className="tooltip">
                                <IconButton
                                    cssClasses="btn--icon-only__basic"
                                    icon={<DownloadIcon fontSize="medium" />}
                                    ariaLabel="Download document"
                                    onClick={() => handleAction('download', params.row)}
                                />
                            </span>
                        </Tooltip>
                        <Tooltip title="Preview Document" placement="bottom" arrow>
                            <span className="tooltip">
                                <IconButton
                                    cssClasses="btn--icon-only__basic"
                                    icon={<PreviewIcon fontSize="medium" />}
                                    ariaLabel="Preview document"
                                    onClick={() => handleAction('preview', params.row)}
                                    />
                            </span>
                        </Tooltip>
                    </div>
                );
            }
        }
    ];

    return (
        <div className="page mydocuments">
            {/*<div className="mydocuments__filters">*/}
            {/*    <input*/}
            {/*        type="text"*/}
            {/*        placeholder="Search document name..."*/}
            {/*        value={searchText}*/}
            {/*        onChange={(e) => setSearchText(e.target.value)}*/}
            {/*        className="input__inp"*/}
            {/*    />*/}

            {/*    <DateRangeFilter*/}
            {/*        onFilterChange={handleFilterChange}*/}
            {/*        cssClasses="date-range-filter--abs"*/}
            {/*        resetTrigger={resetTrigger}*/}
            {/*    />*/}

            {/*    <button className="btn btn--secondary" onClick={handleReset}>*/}
            {/*        Reset*/}
            {/*    </button>*/}
            {/*</div>*/}

            <div className="mydocuments__grid">
                <DataGrid
                    rows={documents}
                    columns={columns}
                    disableColumnMenu
                    disableRowSelectionOnClick
                    loading={loading}
                    pageSizeOptions={[12]}
                    getRowHeight={() => 'auto'}
                    initialState={{ pagination: { paginationModel: { pageSize: 12 } } }}
                    sx={{
                        '& .MuiDataGrid-columnSeparator': { display: 'none' },
                        '& .MuiDataGrid-cell:focus': { outline: 'none' },
                        '& .MuiDataGrid-cell:focus-within': { outline: 'none' },
                        '& .MuiDataGrid-cell': {
                            display: 'flex !important',
                            alignItems: 'center',
                            whiteSpace: 'normal !important',
                            overflow: 'visible !important',
                            lineHeight: 1.4,
                            paddingTop: '8px',
                            paddingBottom: '8px',
                        },

                        '& .MuiDataGrid-columnHeaderTitle': {
                            whiteSpace: 'normal',
                            lineHeight: 1.4,
                            overflow: 'visible',
                        },
                    }}
                    slotProps={{
                        loadingOverlay: {
                            variant: 'circular-progress',
                            noRowsVariant: 'circular-progress',
                        },
                    }}
                />
            </div>
        </div>
    );
};

export default MyDocuments;
